import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import listPlugin from "@fullcalendar/list";
import FullCalendar from "@fullcalendar/react";
import timeGridPlugin from "@fullcalendar/timegrid";
import { useMutation, useQuery } from "@tanstack/react-query";
import moment from "moment";
import { useRef, useState } from "react";
import { AiOutlineCloudSync, AiOutlineFileSync } from "react-icons/ai";
import { CiFilter } from "react-icons/ci";
import { IoMdAdd } from "react-icons/io";
import { MdAssignmentInd } from "react-icons/md";
import { IoIosUndo, IoIosRedo } from "react-icons/io";
import Swal from "sweetalert2";
import FilterModal from "../../common/FilterModal";
import Flatpickr from "flatpickr";
import "flatpickr/dist/flatpickr.min.css";
import dayjs from "dayjs";
import {
  assignEvents,
  deleteEvent,
  getEvents,
  redoEvent,
  undoEvent,
  updateEvent,
  lockEvent,
  updateEventStatus,
  updateEventSteps,
  getHistory,
} from "../../services/services";
import { toastAlert } from "../../utils/SweetAlert";
import { constant } from "../../utils/constants";
import { setCopiedEventReference } from "../../utils/eventClipboard";
import AddEvent from "./AddEvent";

const Calendar = () => {
  const clickTimer = useRef(null);
  const [show, setShow] = useState(false);
  const handleShow = () => {
    let showdata = true;
    if (currentView == "timeGridDay") {
      const currentDate = calendarRef.current.getApi().getDate();
      showdata = { date: currentDate };
    }
    setShow(showdata);
  };
  const calendarRef = useRef(null);
  const [currentView, setCurrentView] = useState("");
  const [filterShow, setFilterShow] = useState(false);
  const [filterData, setFilterData] = useState({});
  const handleFilterShow = () => setFilterShow(true);

  const [currentRange, setCurrentRange] = useState({
    start: null, 
    end: null
  });
  const eventStartDate = currentRange.start?.split('T')?.[0] ?? undefined
  const eventEndDate = currentRange.end?.split('T')?.[0] ?? undefined
  const range = eventStartDate && eventEndDate ? `${eventStartDate}TO${eventEndDate}` : undefined;

  const handleLock = async (id) => {
    return Swal.fire({
      title: "Are you sure?",
      text: "You are going to open this event!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#0d1227",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, Open it!",
    }).then((result) => {
      if (result.isConfirmed) {
        lockMutation.mutateAsync(id);
      }
    });
  };

  const handleCopyEvent = (id) => {
    const clipboard = setCopiedEventReference(id);
    if (!clipboard) {
      toastAlert("error", "Unable to copy this event");
      return;
    }

    toastAlert("success", "Event copied for 24 hours");
  };

  const lockMutation = useMutation({
    mutationFn: async (id) => {
      return await lockEvent({ id: id, locked: true });
    },
    onSuccess: () => {
      toastAlert("success", "Event opened successfully");
      refetch();
    },
  });

  const getEventStyle = (eventObj, employeeDuration) => {
    let classname = "";
    let backgroundColor = "red";
    let textColor = "black";
    let isOverBooked = false;
    const startDate = new Date(eventObj?.startTime);
    const fullDate = startDate.toISOString().split("T")[0];
    const isCancelled = eventObj.status === constant.EVENT_STATUS.CANCELLED;
    const isDenied = eventObj.status === constant.EVENT_STATUS.REJECTED;

    if (
      employeeDuration &&
      employeeDuration[fullDate] &&
      employeeDuration[fullDate][eventObj.employeeId] &&
      employeeDuration[fullDate][eventObj.employeeId] >
        constant.MAX_DURATION_PER_EMPLOYEE
    ) {
      isOverBooked = true;
    }
    if (eventObj.locked === true) {
      classname = "locked_event";
      backgroundColor = "purple";
      textColor = "white";
    } else if (isCancelled) {
      classname = "cancelled_event";
      backgroundColor = "blue";
      textColor = "white";
    } else if (eventObj?.employeeId && eventObj?.steps == 2) {
      classname = "accepted_event";
      backgroundColor = "green";
    } else if (
      eventObj?.status === "accepted" ||
      eventObj?.status === "confirmed"
    ) {
      classname = "employee_sync";
      backgroundColor = "green";
    } else if (isDenied) {
      classname = "denied_event";
      backgroundColor = "brown";
    } else if (!eventObj?.employeeId || eventObj?.employeeId == "") {
      classname = "unassigned_event";
      backgroundColor = "red";
    } else if (eventObj?.employeeId && isOverBooked && eventObj.steps == 1) {
      classname = "notsync_event";
      backgroundColor = "green";
    } else if (eventObj?.employeeId && isOverBooked) {
      classname = "overbooked_event";
      backgroundColor = "green";
    } else if (eventObj?.employeeId && eventObj?.steps == 0) {
      classname = "overbooked_event";
      backgroundColor = "red";
    } else if (eventObj?.employeeId && eventObj?.steps == 1) {
      classname = "notsync_event";
      backgroundColor = "red";
    } else if (isOverBooked) {
      classname = "synced_assigned_event";
      backgroundColor = "green";
    }

    return { backgroundColor, className: classname, textColor };
  };

  const { data, refetch } = useQuery({
    queryKey: ["event-list", currentView, filterData, range],
    queryFn: async ({ signal }) => {
      let params = {
        sortedBy: "startTime",
        sortBy: "ASC",
        range,
      };

      if (
        Object.keys(filterData).length &&
        Object.keys(filterData).filter((item) => filterData[item]["value"])
          .length
      ) {
        Object.keys(filterData).forEach(
          (key) => (params[key] = filterData[key]["value"])
        );
      }

      const resp = await getEvents(params, signal);

      let calendarEvents = [];

      if (
        resp.data &&
        resp?.data?.data?.events &&
        resp?.data?.data?.events?.length
      ) {
        let employeeDuration = {};
        calendarEvents = resp?.data?.data?.events.map((event) => {
          const clientDetails = event?.clientDetails || null;
          const employeeDetails = event?.employeeDetails || null;
          const startDate = new Date(event?.startTime);
          const fullDate = startDate.toISOString().split("T")[0];
          if (event.employeeId && event.employeeId !== "") {
            if (employeeDuration[fullDate]) {
              const innerObject = { ...employeeDuration[fullDate] };
              if (innerObject[event.employeeId]) {
                innerObject[event.employeeId] += event.duration;
              } else {
                innerObject[event.employeeId] = event.duration;
              }
              employeeDuration[fullDate] = innerObject;
            } else
              employeeDuration[fullDate] = {
                [event.employeeId]: event.duration,
              };
          }

          const styleProperies = getEventStyle(event, employeeDuration);
          let isOverBooked = false;
          if (
            employeeDuration &&
            employeeDuration[fullDate] &&
            employeeDuration[fullDate][event.employeeId] &&
            employeeDuration[fullDate][event.employeeId] >
              constant.MAX_DURATION_PER_EMPLOYEE
          ) {
            isOverBooked = true;
          }

          const note = event?.note?.trim() || "N/A";
          const shortNote =
            note.length > 50 ? `${note.slice(0, 50)}...` : note;

          const employeeName = event.employeeId
            ? `${employeeDetails?.firstName ?? ""} ${
                employeeDetails?.lastName ?? ""
              }${isOverBooked ? " (OT)" : ""}${
                event.status === "accepted"
                  ? " (Accepted)"
                  : event.status === "confirmed"
                  ? " (Confirmed)"
                  : event.status === "rejected"
                  ? " (Denied)"
                  : ""
              }`
            : "N/A";
            
          const fullTitle = `Client: ${clientDetails?.firstName ?? ""} ${
            clientDetails?.lastName ?? ""
          }
Employee: ${employeeName}
Notes: ${note}`;

          const displayTitle = `Client: ${clientDetails?.firstName ?? ""} ${
            clientDetails?.lastName ?? ""
          }
Employee: ${employeeName}
${currentView === "timeGridWeek" || currentView === "timeGridDay"? `Notes: ${shortNote}`:""}`;

          return {
            start: new Date(event?.startTime),
            end: new Date(event?.endTime),
            title: displayTitle,
            description: note,
          
            extendedProps: {
              ...event,
              client: clientDetails,
              employee: employeeDetails,
            
              // Used for tooltip
              fullTitle,
            },
            ...styleProperies,
          };
          // return {
          //   start: new Date(event?.startTime),
          //   end: new Date(event?.endTime),
          //   title: `Client: ${clientDetails?.firstName ?? ""} ${
          //     clientDetails?.lastName ?? ""
          //   },\nEmployee: ${
          //     event.employeeId
          //       ? `${employeeDetails?.firstName ?? ""} ${
          //           employeeDetails?.lastName ?? ""
          //         }${isOverBooked ? " (OT)" : ""} ${
          //           event.status === "accepted"
          //             ? " (Accepted)"
          //             : event.status === "confirmed"
          //             ? " (Confirmed)"
          //             : event.status === "rejected"
          //             ? " (Denied)"
          //             : ""
          //         }`
          //       : "N/A"
          //   }${
          //     currentView === "timeGridWeek" || currentView === "timeGridDay"
          //       ? `,\nNotes:${event?.note || "N/A"}`
          //       : ""
          //   }`,
          //   description: event?.note ?? "",
          //   extendedProps: {
          //     ...event,
          //     client: clientDetails,
          //     employee: employeeDetails,
          //   },
          //   ...styleProperies,
          // };
        });
      }
      return calendarEvents;
    },
    staleTime: 1000 * 60, // stale time 1 min - the time for which the data is considered fresh and will not be refetched
    enabled: !!range
  });

  const handleDatesSet = (dateInfo) => {
    // Save the new string representations of the range to state
    setCurrentRange({
      start: dateInfo.startStr,
      end: dateInfo.endStr,
    });
    setCurrentView(dateInfo.view.type);
  };

  const handleSyncEmployee = async () => {
    let selectedDate = null;

    Swal.fire({
      title: "Select a Date",
      html: '<input type="text" placeholder="yyyy-mm-dd" id="datepicker" class="swal2-input">',
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#0d1227",
      cancelButtonColor: "#d33",
      confirmButtonText: "Sync with Employee",
      preConfirm: () => {
        const dateInput = document.getElementById("datepicker");
        if (!dateInput?.value) {
          Swal.showValidationMessage("Please select a valid date!");
          return false;
        }
        return dateInput.value;
      },
      didOpen: () => {
        Flatpickr("#datepicker", {
          enableTime: false,
          minDate: "today",
          dateFormat: "Y-m-d",
          onChange: (selectedDates) => {
            selectedDate = dayjs(selectedDates[0]).format("YYYY-MM-DD");
          },
        });
      },
    }).then(async (result) => {
      if (result.isConfirmed && selectedDate) {
        try {
          syncToEmployee.mutate({ selectedDate });
        } catch (err) {
          console.log("err", err);
        }
      }
    });
  };

  const handleSyncClient = async () => {
    let selectedDate = null;

    Swal.fire({
      title: "Select a Date",
      html: '<input type="text" placeholder="yyyy-mm-dd" id="datepicker" class="swal2-input">',
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#0d1227",
      cancelButtonColor: "#d33",
      confirmButtonText: "Sync with Client",
      preConfirm: () => {
        const dateInput = document.getElementById("datepicker");
        if (!dateInput?.value) {
          Swal.showValidationMessage("Please select a valid date!");
          return false;
        }
        return dateInput.value;
      },
      didOpen: () => {
        Flatpickr("#datepicker", {
          enableTime: false,
          dateFormat: "Y-m-d",
          minDate: "today",
          onChange: (selectedDates) => {
            selectedDate = dayjs(selectedDates[0]).format("YYYY-MM-DD");
          },
        });
      },
    }).then(async (result) => {
      if (result.isConfirmed && selectedDate) {
        try {
          syncToClient.mutate({ selectedDate });
        } catch (err) {
          console.log("Error syncing client:", err);
        }
      }
    });
  };

  const handleAutoAssign = async () => {
    let selectedDate = null;

    Swal.fire({
      title: "Select a Date",
      html: '<input type="text" placeholder="yyyy-mm-dd" id="datepicker" class="swal2-input">',
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#0d1227",
      cancelButtonColor: "#d33",
      confirmButtonText: "Assign",
      preConfirm: () => {
        const dateInput = document.getElementById("datepicker");
        if (!dateInput?.value) {
          Swal.showValidationMessage("Please select a valid date!");
          return false;
        }
        return dateInput.value;
      },
      didOpen: () => {
        Flatpickr("#datepicker", {
          enableTime: false,
          dateFormat: "Y-m-d",
          minDate: "today",
          onChange: (selectedDates) => {
            selectedDate = dayjs(selectedDates[0]).format("YYYY-MM-DD");
          },
        });
      },
    }).then(async (result) => {
      if (result.isConfirmed && selectedDate) {
        try {
          assignMutation.mutate({ selectedDate });
        } catch (err) {
          console.log("Error assigning events:", err);
        }
      }
    });
  };

  const handleDateClick = (info) => {
    const clickedDate = new Date(info.date);
    const today = new Date();

    if (currentView == "timeGridDay" || currentView == "timeGridWeek") {
      setShow(info);
    } else {
      const selectedDate = info.dateStr;
      if (calendarRef.current) {
        const calendarApi = calendarRef.current.getApi();
        calendarApi.changeView("timeGridDay", selectedDate);
      }
    }
  };

  const handleDragging = ({ event, revert }) => {
    if (event?.extendedProps && event?.extendedProps.isCancelled) {
      toastAlert("warning", "Cannot update cancelled events");
      revert();
      return;
    }
    const updatedEnd = moment(event?.end);
    const updatedStart = moment(event?.start);
    const newEventStart = moment(event?.extendedProps?.startTime);
    if (
      !newEventStart.isValid() ||
      !updatedStart.isSame(newEventStart, "day")
    ) {
      revert();
      toastAlert("warning", "Cannot change date of the event");
      return;
    }

    const update = {
      override: false,
      id: event?.extendedProps?._id,
      clientId: event?.extendedProps.clientId,
      employeeId: event?.extendedProps.employeeId || null,
      note: event?.extendedProps?.note || "",
      startTime: updatedStart.format(constant.DATE_FORMAT),
      endTime: updatedEnd.format(constant.DATE_FORMAT),
      cancelEvent: event?.extendedProps?.status === "cancelled" ? true : false,
      locked: event?.extendedProps?.locked || false,
    };
    mutation.mutate({ data: update, revert });
  };

  const syncToEmployee = useMutation({
    mutationFn: async (date) => {
      return await updateEventSteps({ steps: 1, tillDate: date?.selectedDate });
    },
    onSuccess: async (resp) => {
      if (resp.data.statusCode == constant.STATUS_CODE.SUCCESS) {
        toastAlert("success", resp.data.message);
      }
      refetch();
    },
  });

  const syncToClient = useMutation({
    mutationFn: async (date) => {
      return await updateEventSteps({ steps: 2, tillDate: date?.selectedDate });
    },
    onSuccess: async (resp) => {
      if (resp.data.statusCode == constant.STATUS_CODE.SUCCESS) {
        toastAlert("success", resp?.data.message);
      }
      refetch();
    },
  });

  const assignMutation = useMutation({
    mutationFn: async (date) => {
      const assigningEvents = await assignEvents({
        tillDate: date?.selectedDate,
      });
      return assigningEvents?.data || {};
    },
    onSuccess: async (resp) => {
      if (resp && resp?.message && resp?.statusCode == 200) {
        toastAlert("success", resp.message);
        refetch();
      }
      return;
    },
  });

  const overrideEmployeeAssign = (responseData, arg) => {
    let reason = responseData?.data?.reason
      ? responseData?.data?.reason
      : responseData?.message;
    Swal.fire({
      title: "Are you sure?",
      text: `You are going to assign this event as ${reason?.toLowerCase()} `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#0d1227",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, override!",
    }).then((result) => {
      if (result.isConfirmed) {
        let body = {
          ...arg.data,
          override: true,
        };
        mutation.mutate({ ...arg, data: body });
      }
    });
  };

  const mutation = useMutation({
    mutationFn: (body) => updateEvent(body.data),
    onSuccess: (resp) => {
      toastAlert("success", resp?.data?.message);
      refetch();
    },
    onError: (error, arg) => {
      toastAlert("error", error.response?.data?.message);
      arg.revert();
      if (error.status == 422) {
        return overrideEmployeeAssign(error.response.data, arg);
      }
    },
  });

  const handleCancel = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You are going to cancel this event!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#0d1227",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, cancel it!",
    }).then((result) => {
      if (result.isConfirmed) cancelMutation.mutate(id);
    });
  };

  const cancelMutation = useMutation({
    mutationFn: async (id) => {
      return await updateEventStatus(id, constant.EVENT_STATUS.CANCELLED);
    },
    onSuccess: () => {
      toastAlert("success", "Event has been cancelled");
      refetch();
    },
  });

  const ConfirmMutation = useMutation({
    mutationFn: async (id) => {
      await updateEventStatus(id, constant.EVENT_STATUS.CONFIRMED);
    },
    onSuccess: () => {
      toastAlert("success", "Event has been confirmed");
      refetch();
    },
  });

  const isTodayOrTomorrow = (eventDate) => {
    const startOfToday = moment().startOf("day");
    const endOfTomorrow = moment().add(1, "day").endOf("day");
    const eventMoment = moment(eventDate);
    return eventMoment.isBetween(startOfToday, endOfTomorrow, null, "[]");
  };

  const handleEventClick = async (arg) => {
    clearTimeout(clickTimer.current);
    clickTimer.current = setTimeout(async () => {
      let id = arg?.event?.extendedProps?._id;
      const eventData = arg.event.extendedProps;

      // Fetch event history
      let historyItems = [];
      try {
        const historyResponse = await getHistory(id);
        historyItems = historyResponse?.data?.data || [];
        // Sort history by timestamp, newest to oldest
        historyItems.sort(
          (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
        );
      } catch (error) {
        console.error("Error fetching history:", error);
        toastAlert("error", "Failed to fetch event history");
      }

      // Format history items with bullets
      const formattedHistory = historyItems
        .map((item) => {
          return `<li style="color: #6c757d; font-size: 14px; list-style-type: disc; margin-left: 20px;">${item.message}</li>`;
        })
        .join("");

      const swalInstance = Swal.fire({
        icon: "warning",
        title: "Actions",
        html: `
          <div style="text-align: center;">
            <p style="margin-bottom: 10px; font-size: 16px; font-weight: bold;">Select an action for this event</p>
            ${
              formattedHistory
                ? ` 
                   <div style="max-height: 100px; overflow-y: auto; background-color: #f8f9fa; padding: 10px; border-radius: 5px; text-align: left;">
                     <ul style="list-style: none; padding: 0;">${formattedHistory}</ul>
                   </div>`
                : " "
            }
          </div>
        `,
        showCancelButton: true,
        showConfirmButton: true,
        showDenyButton: true,
        confirmButtonText: "Delete",
        cancelButtonText: "Edit",
        denyButtonText: "Cancel",
        showCloseButton: true,
        reverseButtons: true,
        confirmButtonColor: "#dc3545",
        denyButtonColor: "#00f",
        customClass: {
          actions: "swal-order",
          cancelButton: "swal-edit-btn",
          confirmButton: "swal-delete-btn",
          denyButton: "swal-cancel-btn",
        },
        didOpen: () => {
          const actionsContainer = document.querySelector(".swal2-actions");
            if (actionsContainer) {
              if (true) {
                const lockBtn = document.createElement("button");
                lockBtn.innerText = "Open";
                lockBtn.className = "swal2-styled swal-lock-btn";
              const denyBtn = document.querySelector(".swal2-deny");
              if (denyBtn) {
                denyBtn.insertAdjacentElement("afterend", lockBtn);
              } else {
                actionsContainer.appendChild(lockBtn);
              }
              lockBtn.addEventListener("click", async (e) => {
                e.stopPropagation();
                try {
                  await handleLock(id);
                  swalInstance.close();
                } catch (error) {}
                });
              }

              const copyBtn = document.createElement("button");
              copyBtn.innerText = "Copy";
              copyBtn.className = "swal2-styled swal-copy-btn";
              const openButton = document.querySelector(".swal-lock-btn");
              if (openButton) {
                openButton.insertAdjacentElement("afterend", copyBtn);
              } else {
                const denyBtn = document.querySelector(".swal2-deny");
                if (denyBtn) {
                  denyBtn.insertAdjacentElement("afterend", copyBtn);
                } else {
                  actionsContainer.appendChild(copyBtn);
                }
              }

              copyBtn.addEventListener("click", (e) => {
                e.stopPropagation();
                handleCopyEvent(id);
              });

            if (
              isTodayOrTomorrow(eventData.startTime) &&
              eventData.employeeId &&
              eventData.employeeId !== ""
            ) {
              const confirmBtn = document.createElement("button");
              confirmBtn.innerText = "Confirm";
              confirmBtn.className = "swal2-styled swal-confirm-btn";
              confirmBtn.style.backgroundColor = "#28a745";
              confirmBtn.style.color = "white";

              const lastButton =
                document.querySelector(".swal-lock-btn") ||
                document.querySelector(".swal2-deny") ||
                actionsContainer.lastElementChild;

              if (lastButton) {
                lastButton.insertAdjacentElement("afterend", confirmBtn);
              } else {
                actionsContainer.appendChild(confirmBtn);
              }

              confirmBtn.addEventListener("click", async (e) => {
                e.stopPropagation();
                try {
                  await ConfirmMutation.mutateAsync(id);
                  swalInstance.close();
                  toastAlert("success", "Event confirmed successfully");
                } catch (error) {}
              });
            }
          }
        },
      }).then((result) => {
        if (result.isConfirmed) {
          handleDelete(id);
        } else if (result.isDenied) {
          handleCancel(id);
        } else if (result.dismiss === Swal.DismissReason.cancel) {
          handleEventDoubleClick(arg);
        }
      });
    }, 300);
  };

  const handlePastEventClick = async (arg) => {
    clearTimeout(clickTimer.current);
    clickTimer.current = setTimeout(async () => {
      let id = arg?.event?.extendedProps?._id;
      const eventData = arg.event.extendedProps;

      // Fetch event history
      let historyItems = [];
      try {
        const historyResponse = await getHistory(id);
        historyItems = historyResponse?.data?.data || [];
        // Sort history by timestamp, newest to oldest
        historyItems.sort(
          (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
        );
      } catch (error) {
        console.error("Error fetching history:", error);
        toastAlert("error", "Failed to fetch event history");
      }

      // Format history items with bullets
      const formattedHistory = historyItems
        .map((item) => {
          return `<li style="color: #6c757d; font-size: 14px; list-style-type: disc; margin-left: 20px;">${item.message}</li>`;
        })
        .join("");

      const swalInstance = Swal.fire({
        icon: "warning",
        title: "Actions",
        html: `
          <div style="text-align: center;">
            <p style="margin-bottom: 10px; font-size: 16px; font-weight: bold;">Select an action for this past event</p>
            ${
              formattedHistory
                ? `
                   <div style="max-height: 100px; overflow-y: auto; background-color: #f8f9fa; padding: 10px; border-radius: 5px; text-align: left;">
                     <ul style="list-style: none; padding: 0;">${formattedHistory}</ul>
                   </div>`
                : " "
            }
          </div>
        `,
        showCancelButton: true,
        showConfirmButton: true,
        showDenyButton: true,
        confirmButtonText: "Delete",
        cancelButtonText: "Edit",
        denyButtonText: "Cancel",
        showCloseButton: true,
        reverseButtons: true,
        confirmButtonColor: "#dc3545",
        denyButtonColor: "#00f",
        customClass: {
          actions: "swal-order",
          cancelButton: "swal-edit-btn",
          confirmButton: "swal-delete-btn",
          denyButton: "swal-cancel-btn",
        },
      }).then((result) => {
        if (result.isConfirmed) {
          handleDelete(id);
        } else if (result.isDenied) {
          handleCancel(id);
        } else if (result.dismiss === Swal.DismissReason.cancel) {
          handleEventDoubleClick(arg, true);
        }
      });
    }, 300);
  };

  const handleEventDoubleClick = (arg, pastEvent = false) => {
    clearTimeout(clickTimer.current);
    const clickedEventDate = new Date(arg.event.start);
    setShow({ ...arg, pastEvent: pastEvent });
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#0d1227",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) deleteMutation.mutate(id);
    });
  };

  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      return await deleteEvent(id);
    },
    onSuccess: () => {
      toastAlert("success", "Event has been deleted");
      refetch();
    },
  });

  const handleUndoAction = async () => {
    Swal.fire({
      title: "Are you sure ?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#0d1227",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, Undo!",
    }).then((result) => {
      if (result.isConfirmed) undoMutation.mutate();
    });
  };

  const undoMutation = useMutation({
    mutationFn: undoEvent,
    onSuccess: () => {
      toastAlert("success", "All events has been reverted to last version");
      refetch();
    },
  });

  const handleRedoAction = async () => {
    Swal.fire({
      title: "Are you sure ?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#0d1227",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, Redo!",
    }).then((result) => {
      if (result.isConfirmed) redoMutation.mutate();
    });
  };

  const redoMutation = useMutation({
    mutationFn: redoEvent,
    onSuccess: () => {
      toastAlert("success", "All events has been reverted to last version");
      refetch();
    },
  });

  return (
    <div className="main-content">
      <style>
        {`
          .swal-lock-btn, .swal-copy-btn, .swal-confirm-btn {
            border-radius: 4px;
            border: none;
            font-size: 16px;
            cursor: pointer;
            order:4;
          }
        `}
      </style>
      <div className="commonSearchBar my-3">
        <h4>Calendar</h4>
        <div className="endContent">
          <button
            className="greenBtn calendar-btn"
            type="button"
            title="Sync with employee"
            onClick={handleSyncEmployee}
          >
            <AiOutlineFileSync size={30} />
          </button>
          <button
            className="greenBtn calendar-btn"
            type="button"
            title="Sync with client"
            onClick={handleSyncClient}
          >
            <AiOutlineCloudSync size={35} />
          </button>

          <div className="position-relative">
            {Object.keys(filterData).length &&
            Object.keys(filterData).filter((item) => filterData[item]["value"])
              .length ? (
              <div className="redDot" />
            ) : (
              ""
            )}
            <button
              className="greenBtn calendar-btn filter_button_container"
              type="button"
              title="Filter By"
              onClick={handleFilterShow}
            >
              <CiFilter size={35} />
            </button>
          </div>
          <button
            className="greenBtn calendar-btn"
            type="button"
            title="Undo Actions"
            onClick={handleUndoAction}
          >
            <IoIosUndo size={30} />
          </button>
          <button
            className="greenBtn calendar-btn"
            type="button"
            title="Redo Actions"
            onClick={handleRedoAction}
          >
            <IoIosRedo size={30} />
          </button>
          <button
            className="greenBtn calendar-btn"
            type="button"
            title="Auto Assign"
            onClick={handleAutoAssign}
          >
            <MdAssignmentInd size={30} />
          </button>
          <button
            className="greenBtn calendar-btn"
            type="button"
            title="Add Event"
            onClick={handleShow}
          >
            <IoMdAdd size={35} />
          </button>
        </div>
      </div>
      <FullCalendar
        ref={calendarRef}
        // datesSet={(arg) => {
        //   console.log('dataset ++++++++++ ',arg);
        //   setCurrentView(arg.view.type);
        // }}
        datesSet={handleDatesSet}
        plugins={[dayGridPlugin, interactionPlugin, timeGridPlugin, listPlugin]}
        events={data}
        editable={true}
        eventResizableFromStart={true}
        eventResize={handleDragging}
        eventDrop={handleDragging}
        dayMaxEventRows={true}
        nowIndicator={true}
        initialView="dayGridMonth"
        headerToolbar={{
          left: "prev,next today",
          center: "title",
          right: "dayGridMonth,timeGridWeek,timeGridDay,listWeek",
        }}
        dateClick={handleDateClick}
        eventClick={(el) => {
          const clickedEventDate = new Date(el.event.start);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          if (clickedEventDate >= today) {
            handleEventClick(el);
          } else {
            handlePastEventClick(el);
          }
        }}
        // eventContent={(eventInfo) => {
        //   const eventTitle = eventInfo.event.title;
        //   const isLockedOrCancelled =
        //     eventInfo.event.extendedProps?.locked ||
        //     eventInfo.event.extendedProps?.status ===
        //       constant.EVENT_STATUS.CANCELLED;

        //   const forceWhiteText =
        //     (eventInfo.view.type === "timeGridWeek" ||
        //       eventInfo.view.type === "timeGridDay") &&
        //     isLockedOrCancelled;

        //   let updatedTitle = eventTitle.replace(
        //     /(\(CONFIRMED\)|CONFIRMED)/gi,
        //     '<span style="color: green;">$1</span>'
        //   );

        //   updatedTitle = updatedTitle.replace(
        //     /(\(OT\)|OT)/g,
        //     '<span style="color: red;">$1</span>'
        //   );

        //   const isMonthView = eventInfo.view.type === "dayGridMonth";
        //   const isListView = eventInfo.view.type === "listWeek";

        //   return (
        //     <div
        //       className="fc-event"
        //       style={{
        //         display: "flex",
        //         alignItems: "center",
        //       }}
        //     >
        //       {isMonthView && (
        //         <span
        //           className="fc-event-dot"
        //           style={{
        //             backgroundColor: eventInfo.event.backgroundColor || "blue",
        //             borderRadius: "50%",
        //             display: "inline-block",
        //             width: "8px",
        //             height: "8px",
        //             marginRight: "5px",
        //           }}
        //         ></span>
        //       )}

        //       <div className="fc-event-title calender-data">
        //         <b
        //           className="me-2"
        //           style={{ color: forceWhiteText ? "white" : "inherit" }}
        //         >
        //           {eventInfo.timeText}
        //         </b>{" "}
        //         <b
        //           className={`${isMonthView ? "calender-span" : ""}`}
        //           dangerouslySetInnerHTML={{
        //             __html: updatedTitle,
        //           }}
        //           style={{ color: forceWhiteText ? "white" : "inherit" }}
        //         />
        //       </div>
        //     </div>
        //   );
        // }}

        eventDidMount={(info) => {
          info.el.setAttribute("title", info.event.extendedProps.fullTitle || info.event.title);
          info.el.addEventListener("dblclick", () =>
            handleEventDoubleClick(info)
          );
          info.el.addEventListener("click", () => {
            const clickedEventDate = new Date(info.event.start);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            if (clickedEventDate >= today) {
              handleEventClick(info);
            } else {
              handlePastEventClick(info);
            }
          });
        }}
        eventContent={(eventInfo) => {
          const eventTitle = eventInfo.event.title;
          const isLockedOrCancelled =
            eventInfo.event.extendedProps?.locked ||
            eventInfo.event.extendedProps?.status ===
              constant.EVENT_STATUS.CANCELLED;

          const forceWhiteText =
            (eventInfo.view.type === "timeGridWeek" ||
              eventInfo.view.type === "timeGridDay") &&
            isLockedOrCancelled;

          let updatedTitle = eventTitle
            // Show each field on its own line
            .replace(/\n/g, "<br/>")
            // Green confirmed
            .replace(
              /(\(Confirmed\)|Confirmed)/gi,
              '<span style="color:green;">$1</span>'
            )
            // Green accepted
            .replace(
              /(\(Accepted\)|Accepted)/gi,
              '<span style="color:green;">$1</span>'
            )
            // Brown denied
            .replace(
              /(\(Denied\)|Denied)/gi,
              '<span style="color:brown;">$1</span>'
            )
            // Red OT
            .replace(
              /(\(OT\)|OT)/g,
              '<span style="color:red;">$1</span>'
            );
          
          const isMonthView = eventInfo.view.type === "dayGridMonth";

          return (
            <div
              className="fc-event"
              style={{
                display: "flex",
                alignItems: "flex-start",
                width: "100%",
              }}
            >
              {isMonthView && (
                <span
                  className="fc-event-dot"
                  style={{
                    backgroundColor:
                      eventInfo.event.backgroundColor || "blue",
                    borderRadius: "50%",
                    width: "8px",
                    height: "8px",
                    marginRight: "5px",
                    marginTop: "5px",
                    flexShrink: 0,
                  }}
                />
              )}
        
              <div
                // className="fc-event-title calender-data"
                className="fc-event-title"
                style={{
                  whiteSpace: "normal",
                  lineHeight: "1.3",
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <b
                  // className="me-2"
                  style={{
                    color: forceWhiteText ? "white" : "inherit",
                  }}
                >
                  {eventInfo.timeText}
                </b>
                
                <span
                  // className={isMonthView ? "calender-span" : ""}
                  dangerouslySetInnerHTML={{
                    __html: `<br/>${updatedTitle}`,
                  }}
                  style={{
                    color: forceWhiteText ? "white" : "inherit",
                  }}
                />
              </div>
            </div>
          );
        }}
      />
      {filterShow && (
        <FilterModal
          show={filterShow}
          setShow={setFilterShow}
          refetch={refetch}
          data={filterData}
          setData={setFilterData}
          fromAdmin={true}
        />
      )}
      {show && <AddEvent show={show} setShow={setShow} currentView={currentView} filterData={filterData} range={range} />}
    </div>
  );
};

export default Calendar;
